/**
 * subscriptionId:
 *  1. is like a shopping card id
 *  2. we get the subscriptionId back after POST a planId to /checkout endpoint
 *  3. there is no endpoint to remove an item from shopping cart,
 *     so when user removes an item, we just need to get rid of current subId and start over
 */

import { applyReducers, DoneHandler } from '@m/effector-utils';
import {
  ExistingSubscriptions,
  NewSubscriptionCheckoutResponse,
  Site,
  Subscription,
  SubscriptionPlan,
  SubscriptionSite,
} from '@m/types/api';
import { createStore, Effect } from 'effector';

import {
  addLegacySubscriptionToCart,
  addSubscriptionToCart,
  cancelSubscription,
  getActiveSubscriptions,
  getAvailableAndActiveSubscriptions,
  makePaymentForSubscription,
  resetSubscriptionAsyncStatuses,
} from './actions';

import type { APIResponse } from '@m/utils/http';

export type SubscriptionState = {
  cart?: { subscription: Subscription | NewSubscriptionCheckoutResponse };
  currentSubscriptionId: number | null;
  currentSubscriptionUuid: string | null;
  existingSubscriptions: Subscription[];
  recommendedSites: Site[];
  selectedSite: Site | null;
  sites: Site[];
  sitesMap: {
    [siteId: number]: Site;
  };
  subscriptionPlans: {
    [siteId: number]: SubscriptionPlan[];
  };
  upcomingSubscriptions: Subscription[];
};

const initialState: SubscriptionState = {
  currentSubscriptionId: null,
  currentSubscriptionUuid: null,
  sites: [],
  selectedSite: null,
  existingSubscriptions: [],
  upcomingSubscriptions: [],
  recommendedSites: [],
  subscriptionPlans: {},
  sitesMap: {},
};

const store = createStore(initialState);

type Reducers = {
  addLegacySubscriptionToCart: {
    action: Effect<Parameters<typeof addLegacySubscriptionToCart>[0], APIResponse>;
    done: DoneHandler<
      Parameters<typeof addLegacySubscriptionToCart>[0],
      SubscriptionState,
      Awaited<ReturnType<typeof addLegacySubscriptionToCart>>
    >;
  };
  addSubscriptionToCart: {
    action: Effect<Parameters<typeof addSubscriptionToCart>[0], APIResponse>;
    done: DoneHandler<
      Parameters<typeof addSubscriptionToCart>[0],
      SubscriptionState,
      Awaited<ReturnType<typeof addSubscriptionToCart>>
    >;
  };
  cancelSubscription: {
    action: Effect<Parameters<typeof cancelSubscription>[0], APIResponse>;
    done: DoneHandler<
      Parameters<typeof cancelSubscription>[0],
      SubscriptionState,
      Awaited<ReturnType<typeof cancelSubscription>>
    >;
  };
  getActiveSubscriptions: {
    action: Effect<Parameters<typeof getActiveSubscriptions>[0], APIResponse>;
    done: DoneHandler<
      Parameters<typeof getActiveSubscriptions>[0],
      SubscriptionState,
      Awaited<ReturnType<typeof getActiveSubscriptions>>
    >;
  };
  getAvailableAndActiveSubscriptions: {
    action: Effect<Parameters<typeof getAvailableAndActiveSubscriptions>[0], APIResponse>;
    done: DoneHandler<
      Parameters<typeof getAvailableAndActiveSubscriptions>[0],
      SubscriptionState,
      Awaited<ReturnType<typeof getAvailableAndActiveSubscriptions>>
    >;
  };
  makePaymentForSubscription: {
    action: Effect<Parameters<typeof makePaymentForSubscription>[0], APIResponse>;
  };
};

export const reducers: Reducers = {
  getAvailableAndActiveSubscriptions: {
    action: getAvailableAndActiveSubscriptions,
    done: (state, { result: { data } }) => {
      const subscriptionPlans: { [siteId: number]: SubscriptionPlan[] } = {};
      const sitesMap: { [siteId: number]: Site } = {};

      const { sites: sitesResp, existingSubscriptions } = data;

      const existingSubSiteIds =
        existingSubscriptions?.map((existingSub: ExistingSubscriptions) => existingSub.site.id) ||
        [];

      const sites = sitesResp.map((subscriptionSites: SubscriptionSite) => {
        subscriptionPlans[subscriptionSites.site.id] = subscriptionSites.subscriptionPlans;
        sitesMap[subscriptionSites.site.id] = subscriptionSites.site;
        return subscriptionSites.site;
      });

      const recommendedSites = sites.filter((site: Site) => !existingSubSiteIds.includes(site.id));

      return {
        ...state,
        recommendedSites,
        sites,
        subscriptionPlans,
        sitesMap,
      };
    },
  },
  getActiveSubscriptions: {
    action: getActiveSubscriptions,
    done: (state, { result: { data = [] } }) => {
      const sitesMap: { [siteId: number]: Site } = {};

      const supscriptionSplit = data.reduce(
        (
          acc: { existing: ExistingSubscriptions[]; upcoming: ExistingSubscriptions[] },
          curr: ExistingSubscriptions,
        ) => {
          sitesMap[curr.site.id] = curr.site;

          if (Number(curr.subscription.details?.accessStart) < Date.now()) {
            acc.existing.push(curr);
            return acc;
          }
          if (curr.subscription.details?.accessEnd === curr.subscription.details?.accessStart) {
            return acc;
          }

          acc.upcoming.push(curr);
          return acc;
        },
        {
          existing: [],
          upcoming: [],
        },
      );

      const existingSubscriptions = supscriptionSplit.existing.map(
        (existingSubscription: ExistingSubscriptions) => {
          const nextExistingSubscription = { ...existingSubscription };
          nextExistingSubscription.subscription.enterpriseDetails =
            existingSubscription.enterpriseDetails;
          nextExistingSubscription.subscription.site = existingSubscription.site;
          // We are hardcoding the after hour label for the Ally Bank time based access subscription.
          // We'll need to eventually get this from the BE=
          if (
            nextExistingSubscription?.subscription?.id === 56817 &&
            nextExistingSubscription?.subscription?.details?.label === null
          ) {
            nextExistingSubscription.subscription.details.label = { AssignmentType: 'After Hours' };
          }
          nextExistingSubscription.subscription.site = existingSubscription.site;
          return nextExistingSubscription.subscription;
        },
      );

      const upcomingSubscriptions = supscriptionSplit.upcoming.map(
        (existingSubscription: ExistingSubscriptions) => {
          const nextExistingSubscription = { ...existingSubscription };
          nextExistingSubscription.subscription.enterpriseDetails =
            existingSubscription.enterpriseDetails;
          nextExistingSubscription.subscription.site = existingSubscription.site;
          return nextExistingSubscription.subscription;
        },
      );

      return {
        ...state,
        sitesMap: { ...state.sitesMap, ...sitesMap },
        existingSubscriptions,
        upcomingSubscriptions,
      };
    },
  },
  addSubscriptionToCart: {
    action: addSubscriptionToCart,
    done: (state, { result: { data } = {} }) => {
      const cart = {
        subscription: data as NewSubscriptionCheckoutResponse,
      };

      return {
        ...state,
        currentSubscriptionId: null,
        currentSubscriptionUuid: data?.subscriptionPlanId ?? null,
        cart,
      };
    },
  },
  addLegacySubscriptionToCart: {
    action: addLegacySubscriptionToCart,
    done: (state, { result: { data } = {} }) => ({
      ...state,
      currentSubscriptionId: data?.subscription.id ?? null,
      currentSubscriptionUuid: null,
      cart: data,
    }),
  },
  makePaymentForSubscription: {
    action: makePaymentForSubscription,
  },
  cancelSubscription: {
    action: cancelSubscription,
    done: (state, { result: { success, data } }) => {
      if (success) {
        const nextExistingSubs = [...state.existingSubscriptions];
        const canceledSubIndex = nextExistingSubs.findIndex(
          (sub) => sub.id === data.subscriptionId,
        );
        nextExistingSubs.splice(canceledSubIndex, 1);

        return {
          ...state,
          existingSubscriptions: nextExistingSubs,
        };
      }
      return state;
    },
  },
};

const asyncResetReducers = [resetSubscriptionAsyncStatuses];

export default applyReducers({
  storeName: 'shared/subscription',
  store,
  asyncResetReducers,
  reducers,
});
